import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

// ──────────────────────────────────────────────────────────────────────────────

interface ResetPasswordModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  profileId: string;
  role: 'teacher' | 'student';
  name: string;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  visible,
  onHide,
  onSuccess,
  profileId,
  role,
  name,
}) => {
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState<string | null>(null);
  const [success, setSuccess]                 = useState(false);

  const [errors, setErrors] = useState<{ newPassword: string; confirmPassword: string }>({
    newPassword: '', confirmPassword: '',
  });

  const clearError = (field: 'newPassword' | 'confirmPassword') => {
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = { newPassword: '', confirmPassword: '' };
    let ok = true;
    if (!newPassword.trim()) {
      e.newPassword = 'New password is required'; ok = false;
    } else if (newPassword.length < 6) {
      e.newPassword = 'Password must be at least 6 characters'; ok = false;
    }
    if (!confirmPassword.trim()) {
      e.confirmPassword = 'Please confirm the password'; ok = false;
    } else if (newPassword !== confirmPassword) {
      e.confirmPassword = 'Passwords do not match'; ok = false;
    }
    setErrors(e);
    return ok;
  };

  const handleReset = async () => {
    if (!validate()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/v1/operator/reset-password-by-id', {
        profile_id:       profileId,
        role,
        new_password:     newPassword,
        confirm_password: confirmPassword,
      });
      if (response.data.success) {
        setSuccess(true);
        if (onSuccess) onSuccess();
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setErrors({ newPassword: '', confirmPassword: '' });
    setError(null);
    setSuccess(false);
    onHide();
  };

  const roleLabel = role === 'teacher' ? 'Teacher' : 'Student';
  const roleIcon  = role === 'teacher' ? 'pi-chalkboard-teacher' : 'pi-graduation-cap';

  // Password strength helper
  const strength =
    newPassword.length >= 12 ? 4 :
    newPassword.length >= 9  ? 3 :
    newPassword.length >= 6  ? 2 :
    newPassword.length > 0   ? 1 : 0;
  const strengthColors = ['#ef4444', '#f97316', '#eab308', '#22c55e'];
  const strengthLabels = ['Too short', 'Weak', 'Good', 'Strong'];

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '480px' }}
      dismissableMask
      draggable={false}
      closable={!loading}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 60px -12px rgba(14,34,93,0.35)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: 0 } },
      }}
    >
      <div>

        {/* ── Hero Header ──────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          padding: '28px 28px 24px', position: 'relative', overflow: 'hidden', color: C.white,
        }}>
          {/* Ambient orbs */}
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '150px', height: '150px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-50px', left: '-20px', width: '130px', height: '130px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(35px)', pointerEvents: 'none' }} />

          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={loading}
            style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', width: '32px', height: '32px', color: C.white, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', zIndex: 2, transition: 'background 0.2s' }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          >
            <i className="pi pi-times" />
          </button>

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '16px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
              <i className={`pi ${roleIcon}`} style={{ fontSize: '0.65rem' }} />
              {roleLabel} Password Reset
            </div>

            {/* Lock icon + title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="pi pi-lock" style={{ fontSize: '1.4rem', color: C.gold }} />
              </div>
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: '700', color: C.white }}>
                  Reset Password
                </h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                  Setting new password for{' '}
                  <span style={{ color: C.gold, fontWeight: '600' }}>{name}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        <div style={{ padding: '24px 28px 28px', background: C.bg }}>

          {/* ── Success state ─────────────────────────────────────────────── */}
          {success ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(16,185,129,0.25)' }}>
                <i className="pi pi-check" style={{ fontSize: '1.8rem', color: '#065f46' }} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: '#065f46' }}>
                Password Reset Successfully!
              </h3>
              <p style={{ margin: '0 0 24px 0', fontSize: '0.85rem', color: C.sub }}>
                The password for <strong>{name}</strong> has been updated.
                They can now log in with the new password.
              </p>
              <button
                onClick={handleClose}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 28px', background: C.gold, border: 'none', borderRadius: '10px', color: C.navy, fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(251,178,31,0.4)', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <i className="pi pi-check-circle" />
                Close
              </button>
            </div>

          ) : (
            <>
              {/* Info notice */}
              <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)' }}>
                <div style={{ width: '30px', height: '30px', background: C.goldDim, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="pi pi-info-circle" style={{ color: C.gold, fontSize: '0.9rem' }} />
                </div>
                <div>
                  <p style={{ margin: '0 0 2px 0', fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>
                    Resetting password for {roleLabel}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: C.sub, lineHeight: '1.4' }}>
                    The new password will be applied immediately. Inform the {roleLabel.toLowerCase()} after resetting.
                  </p>
                </div>
              </div>

              {/* API error */}
              {error && (
                <Message severity="error" text={error} style={{ marginBottom: '16px', fontSize: '0.85rem', width: '100%' }} />
              )}

              {/* Fields */}
              <div style={{ background: C.white, borderRadius: '12px', padding: '16px', marginBottom: '4px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <GlobalInputField
                  label="New Password"
                  required
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); clearError('newPassword'); }}
                  error={errors.newPassword}
                  type="password"
                  icon="pi pi-lock"
                />
                <GlobalInputField
                  label="Confirm Password"
                  required
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); clearError('confirmPassword'); }}
                  error={errors.confirmPassword}
                  type="password"
                  icon="pi pi-lock"
                />
              </div>

              {/* Password hint */}
              <p style={{ margin: '10px 0 16px 0', fontSize: '0.72rem', color: C.muted, display: 'flex', alignItems: 'center', gap: '5px' }}>
                <i className="pi pi-shield" style={{ fontSize: '0.68rem' }} />
                Minimum 6 characters required
              </p>

              {/* Strength indicator */}
              {strength > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', background: i <= strength ? strengthColors[strength - 1] : C.border, transition: 'background 0.3s ease' }} />
                    ))}
                  </div>
                  <p style={{ margin: 0, fontSize: '0.7rem', color: strength > 0 ? strengthColors[strength - 1] : C.muted, fontWeight: '600' }}>
                    {strengthLabels[strength - 1]}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                {/* Cancel */}
                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.88rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', opacity: loading ? 0.5 : 1 }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; } }}
                  onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; } }}
                >
                  <i className="pi pi-times" style={{ fontSize: '0.88rem' }} />
                  Cancel
                </button>

                {/* Primary — gold */}
                <button
                  onClick={handleReset}
                  disabled={loading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 24px', background: loading ? C.muted : C.gold, border: 'none', borderRadius: '10px', color: loading ? C.white : C.navy, fontSize: '0.88rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : '0 4px 12px rgba(251,178,31,0.4)', transition: 'all 0.2s' }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)'; } }}
                  onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)'; } }}
                >
                  <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'} style={{ fontSize: '0.88rem' }} />
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ResetPasswordModal;


// import React, { useState } from 'react';
// import { Dialog } from 'primereact/dialog';
// import { Message } from 'primereact/message';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
// import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

// interface ResetPasswordModalProps {
//   visible: boolean;
//   onHide: () => void;
//   onSuccess?: () => void;
//   // The profile _id from teachers/students collection
//   profileId: string;
//   // 'teacher' | 'student'
//   role: 'teacher' | 'student';
//   // Display name shown in the UI
//   name: string;
// }

// const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
//   visible,
//   onHide,
//   onSuccess,
//   profileId,
//   role,
//   name,
// }) => {
//   const [newPassword, setNewPassword]         = useState('');
//   const [confirmPassword, setConfirmPassword] = useState('');
//   const [loading, setLoading]                 = useState(false);
//   const [error, setError]                     = useState<string | null>(null);
//   const [success, setSuccess]                 = useState(false);

//   const [errors, setErrors] = useState<{ newPassword: string; confirmPassword: string }>({
//     newPassword: '',
//     confirmPassword: '',
//   });

//   const clearError = (field: 'newPassword' | 'confirmPassword') => {
//     if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
//   };

//   const validate = () => {
//     const e = { newPassword: '', confirmPassword: '' };
//     let ok = true;

//     if (!newPassword.trim()) {
//       e.newPassword = 'New password is required';
//       ok = false;
//     } else if (newPassword.length < 6) {
//       e.newPassword = 'Password must be at least 6 characters';
//       ok = false;
//     }

//     if (!confirmPassword.trim()) {
//       e.confirmPassword = 'Please confirm the password';
//       ok = false;
//     } else if (newPassword !== confirmPassword) {
//       e.confirmPassword = 'Passwords do not match';
//       ok = false;
//     }

//     setErrors(e);
//     return ok;
//   };

//   const handleReset = async () => {
//     if (!validate()) return;

//     setLoading(true);
//     setError(null);

//     try {
//       const response = await apiClient.post('/v1/operator/reset-password-by-id', {
//         profile_id:       profileId,
//         role:             role,
//         new_password:     newPassword,
//         confirm_password: confirmPassword,
//       });

//       if (response.data.success) {
//         setSuccess(true);
//         if (onSuccess) onSuccess();
//       } else {
//         setError(response.data.message || 'Failed to reset password');
//       }
//     } catch (err: any) {
//       setError(
//         err.response?.data?.error ||
//         err.response?.data?.message ||
//         'Failed to reset password. Please try again.'
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleClose = () => {
//     setNewPassword('');
//     setConfirmPassword('');
//     setErrors({ newPassword: '', confirmPassword: '' });
//     setError(null);
//     setSuccess(false);
//     onHide();
//   };

//   const roleLabel = role === 'teacher' ? 'Teacher' : 'Student';
//   const roleColor = role === 'teacher' ? '#0f3d3e' : '#1a5555';
//   const roleIcon  = role === 'teacher' ? 'pi-chalkboard-teacher' : 'pi-graduation-cap';

//   return (
//     <Dialog
//       visible={visible}
//       onHide={handleClose}
//       style={{ width: '90vw', maxWidth: '480px' }}
//       dismissableMask
//       draggable={false}
//       closable={!loading}
//       pt={{
//         root: {
//           style: {
//             borderRadius: '20px',
//             overflow: 'hidden',
//             boxShadow: '0 25px 60px -12px rgba(0,0,0,0.3)',
//           },
//         },
//         header: { style: { display: 'none' } },
//         content: { style: { padding: 0 } },
//       }}
//     >
//       <div>
//         {/* ── Header ───────────────────────────────────────────────────────── */}
//         <div
//           style={{
//             background: `linear-gradient(135deg, ${roleColor} 0%, #1a5555 50%, #0a2f2f 100%)`,
//             padding: '28px 28px 24px',
//             position: 'relative',
//             overflow: 'hidden',
//             color: 'white',
//           }}
//         >
//           {/* Decorative blobs */}
//           <div style={{
//             position: 'absolute', top: '-40px', right: '-40px',
//             width: '140px', height: '140px',
//             background: 'rgba(212,241,126,0.12)', borderRadius: '50%', filter: 'blur(35px)',
//           }} />
//           <div style={{
//             position: 'absolute', bottom: '-60px', left: '-30px',
//             width: '160px', height: '160px',
//             background: 'rgba(212,241,126,0.07)', borderRadius: '50%', filter: 'blur(40px)',
//           }} />

//           {/* Close button */}
//           <button
//             onClick={handleClose}
//             disabled={loading}
//             style={{
//               position: 'absolute', top: '16px', right: '16px',
//               background: 'rgba(255,255,255,0.12)', border: 'none',
//               borderRadius: '8px', width: '32px', height: '32px',
//               color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
//               display: 'flex', alignItems: 'center', justifyContent: 'center',
//               fontSize: '0.8rem', zIndex: 2,
//             }}
//           >
//             <i className="pi pi-times" />
//           </button>

//           <div style={{ position: 'relative', zIndex: 1 }}>
//             {/* Badge */}
//             <div style={{
//               display: 'inline-flex', alignItems: 'center', gap: '6px',
//               padding: '4px 10px',
//               background: 'rgba(212,241,126,0.2)', borderRadius: '16px',
//               marginBottom: '16px', fontSize: '0.65rem', fontWeight: '700',
//               letterSpacing: '0.8px', textTransform: 'uppercase',
//             }}>
//               <i className={`pi ${roleIcon}`} style={{ fontSize: '0.65rem' }} />
//               {roleLabel} Password Reset
//             </div>

//             {/* Lock icon + title */}
//             <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
//               <div style={{
//                 width: '52px', height: '52px', borderRadius: '14px',
//                 background: 'rgba(255,255,255,0.12)',
//                 border: '1px solid rgba(255,255,255,0.2)',
//                 display: 'flex', alignItems: 'center', justifyContent: 'center',
//                 flexShrink: 0,
//               }}>
//                 <i className="pi pi-lock" style={{ fontSize: '1.4rem', color: '#d4f17e' }} />
//               </div>
//               <div>
//                 <h2 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: '700', color: 'white' }}>
//                   Reset Password
//                 </h2>
//                 <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
//                   Setting new password for{' '}
//                   <span style={{ color: '#d4f17e', fontWeight: '600' }}>{name}</span>
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* ── Body ─────────────────────────────────────────────────────────── */}
//         <div style={{ padding: '24px 28px 28px', background: 'white' }}>

//           {/* Success state */}
//           {success ? (
//             <div style={{ textAlign: 'center', padding: '16px 0' }}>
//               <div style={{
//                 width: '64px', height: '64px', borderRadius: '50%',
//                 background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
//                 display: 'flex', alignItems: 'center', justifyContent: 'center',
//                 margin: '0 auto 16px',
//                 boxShadow: '0 8px 24px rgba(16,185,129,0.25)',
//               }}>
//                 <i className="pi pi-check" style={{ fontSize: '1.8rem', color: '#065f46' }} />
//               </div>
//               <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: '#065f46' }}>
//                 Password Reset Successfully!
//               </h3>
//               <p style={{ margin: '0 0 24px 0', fontSize: '0.85rem', color: '#6b7280' }}>
//                 The password for <strong>{name}</strong> has been updated.
//                 They can now log in with the new password.
//               </p>
//               <GlobalButton
//                 label="Close"
//                 variant="primary"
//                 icon="pi pi-check-circle"
//                 onClick={handleClose}
//               />
//             </div>
//           ) : (
//             <>
//               {/* Info notice */}
//               <div style={{
//                 background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
//                 border: '1px solid #bbf7d0',
//                 borderRadius: '12px', padding: '14px 16px',
//                 marginBottom: '20px',
//                 display: 'flex', alignItems: 'flex-start', gap: '10px',
//               }}>
//                 <i className="pi pi-info-circle" style={{ color: '#16a34a', fontSize: '1rem', marginTop: '1px' }} />
//                 <div>
//                   <p style={{ margin: '0 0 2px 0', fontSize: '0.8rem', fontWeight: '700', color: '#15803d' }}>
//                     Resetting password for {roleLabel}
//                   </p>
//                   <p style={{ margin: 0, fontSize: '0.75rem', color: '#166534', lineHeight: '1.4' }}>
//                     The new password will be applied immediately. Inform the {roleLabel.toLowerCase()} after resetting.
//                   </p>
//                 </div>
//               </div>

//               {/* API error */}
//               {error && (
//                 <Message
//                   severity="error"
//                   text={error}
//                   style={{ marginBottom: '16px', fontSize: '0.85rem', width: '100%' }}
//                 />
//               )}

//               {/* Fields */}
//               <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
//                 <GlobalInputField
//                   label="New Password"
//                   required
//                   placeholder="Enter new password"
//                   value={newPassword}
//                   onChange={e => { setNewPassword(e.target.value); clearError('newPassword'); }}
//                   error={errors.newPassword}
//                   type="password"
//                   icon="pi pi-lock"
//                 />
//                 <GlobalInputField
//                   label="Confirm Password"
//                   required
//                   placeholder="Re-enter new password"
//                   value={confirmPassword}
//                   onChange={e => { setConfirmPassword(e.target.value); clearError('confirmPassword'); }}
//                   error={errors.confirmPassword}
//                   type="password"
//                   icon="pi pi-lock"
//                 />
//               </div>

//               {/* Password hint */}
//               <p style={{
//                 margin: '12px 0 20px 0', fontSize: '0.72rem', color: '#9ca3af',
//                 display: 'flex', alignItems: 'center', gap: '5px',
//               }}>
//                 <i className="pi pi-shield" style={{ fontSize: '0.68rem' }} />
//                 Minimum 6 characters required
//               </p>

//               {/* Strength indicator (visual only) */}
//               {newPassword.length > 0 && (
//                 <div style={{ marginBottom: '20px' }}>
//                   <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
//                     {[1, 2, 3, 4].map(i => {
//                       const strength =
//                         newPassword.length >= 12 ? 4 :
//                         newPassword.length >= 9  ? 3 :
//                         newPassword.length >= 6  ? 2 : 1;
//                       const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e'];
//                       return (
//                         <div key={i} style={{
//                           flex: 1, height: '4px', borderRadius: '2px',
//                           background: i <= strength ? colors[strength - 1] : '#e5e7eb',
//                           transition: 'background 0.3s ease',
//                         }} />
//                       );
//                     })}
//                   </div>
//                   <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280' }}>
//                     {newPassword.length < 6  ? 'Too short' :
//                      newPassword.length < 9  ? 'Weak' :
//                      newPassword.length < 12 ? 'Good' : 'Strong'}
//                   </p>
//                 </div>
//               )}

//               {/* Actions */}
//               <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
//                 <GlobalButton
//                   label="Cancel"
//                   variant="outline"
//                   icon="pi pi-times"
//                   onClick={handleClose}
//                   disabled={loading}
//                 />
//                 <GlobalButton
//                   label={loading ? 'Resetting...' : 'Reset Password'}
//                   variant="primary"
//                   icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
//                   onClick={handleReset}
//                   disabled={loading}
//                   loading={loading}
//                 />
//               </div>
//             </>
//           )}
//         </div>
//       </div>
//     </Dialog>
//   );
// };

// export default ResetPasswordModal;